<template >
  <div class="container">
    <div class="col-md-4">
      <div id="form-search-projects" class="card">
        <Search
          v-model="name"
          :focus="searchFocus"
          @search="onSearchChange"
          @clearSearch="onClearSearch"
        />
      </div>
    </div>
    <div class="card clearfix">
      <div v-if="showLoading" class="loading-next-page">
        <LoaderContent />
      </div>
      <DataTable
        ref="table"
        :key="`data-table-key-${keyTable}`"
        :data.sync="dataTable"
        :loading="tableLoading"
        :updatingRow="updatingRowId"
        @onSort="onSort"
      />
    </div>

    <datatable-bulk-actions
      v-if="selectedRows.length"
      v-model="actionTarget"
      type="country"
      :rows="selectedRows"
      :total="countriesTotal"
      :actions="bulkActions"
      translationsPath="country.form.button"
      @cancel="closeEditModal"
      @edit="openEditForm"
    />

    <side-panel v-if="showEditPanel" :onClose="closeEditForm">
      <h3>{{ $tc("country.form.edit.title", selectedRows.length) }}</h3>
      <h4 v-if="selectedRows.length === 1">
        {{ selectedRows[0].name }}
      </h4>
      <appForm
        id="add-outlet-form"
        :key="`form-households${householdModel.households}`"
        :model.sync="householdModel"
        :input-fields="updateForm"
        :showSubmit="true"
        :showCancel="true"
        class="rmt100"
        @onFormSubmit="onBulk"
        @updateModel="updateModel"
        @onCancelClick="closeEditModal"
      />
    </side-panel>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import AppForm from "../form";
import LoaderContent from "../loader";
import SidePanel from "../side-panel";
import searchMixin from "../../mixins/search";
import DataTable from "../../components/datatable";
import DatatableBulkActions from "../../components/datatable-bulk-actions";
import Search from "../Search.vue";
import InputSelect from "../form-components/input-select";
import { inputErrors } from "../../helpers/inputErrorHelper";
import { FIELD_TYPE, VALIDATIONS, BULK_UPDATE } from "../../config/const";

export default {
  name: "CountriesList",

  components: {
    AppForm,
    DataTable,
    SidePanel,
    LoaderContent,
    Search,
    DatatableBulkActions
  },

  mixins: [searchMixin],

  data: function () {
    return {
      name: "",
      keyTable: 1,
      selectedRows: [],
      formErrors: null,
      updatingRowId: null,
      showLoading: false,
      showEditPanel: false,
      actionTarget: "selected",
      householdModel: {
        households: undefined,
      },
      pagination: {
        page: 1,
        per_page: 20,
        order: "name",
        sort: "asc",
      },
    };
  },

  computed: {
    ...mapState({
      countries: (state) => state.admin.countries.countries.data,
      isBusy: (state) => state.admin.countries.countries.isBusy,
    }),
    ...mapGetters("admin/countries", ["countriesTotal"]),
    actionTargetFiltered() {
      return BULK_UPDATE.ACTION_TARGET.FILTERED;
    },
    actionTargetSelected() {
      return BULK_UPDATE.ACTION_TARGET.SELECTED;
    },
    totalSelectedMessage() {
      return this.$tc(
        "country.select_country_amount",
        this.selectedRows.length,
        { amount: this.selectedRows.length }
      );
    },
    projectId() {
      return this.$route.params.project_uid;
    },
    dataGetCountries() {
      return {
        projectId: this.projectId,
        pagination: this.pagination,
        name: this.name,
      };
    },
    selectedIds() {
      return this.selectedRows.map((row) => {
        return row.id;
      });
    },
    tableLoading() {
      return this.showLoading && this.isBusy && this.pagination.page === 1;
    },
    dataTable() {
      const self = this;

      return {
        columns: [
          {
            key: "name",
            label: this.$tc("country.country", 1),
            disableKey: "scoring_default",
            orderable: true,
          },
          {
            key: "household",
            label: this.$tc("country.household", 2),
            input: {
              name: "household",
              type: FIELD_TYPE.NUMBER,
              inputType: "number",
              min: 1,
              styleNames: { color: "#ec9148", border: "none", outline: "none" },
              placeholder: this.$t("country.total_households", 2),
              validate: [VALIDATIONS.NUMBER],
              onBlur: function (value, key, row) {
                if (!value || parseInt(value) <= 1) {
                  value = 1;
                  self.updateCountryHouseholds({
                    country: row,
                    households: value,
                  });
                  self.keyTable++;
                }
                self.updateRow(value, key, row);
              },
            },
            disableKey: "scoring_default",
            orderable: true,
          },
        ],
        body: this.countries ? this.countries.data : [],
        resizable: true,
        order: {
          column: this.pagination.order,
          direction: this.pagination.sort,
          onChange: function (column, direction) {
            self.onSortTable({ order: column, sort: direction });
          },
        },
        bulkActions: {
          enabled: true,
          disableKey: "scoring_default",
          onSelect: function (rows) {
            self.selectedRows = rows;
          },
        },
      };
    },
    updateForm() {
      return {
        households: {
          id: "households",
          name: "households",
          label: "Households",
          type: FIELD_TYPE.INPUT,
          inputType: "number",
          placeholder: this.$t("country.total_households", 2),
          translatePlaceholder: "sources.select_title",
          style: "flex-grow: 1; order: 1;",
          customClass: "",
          classList: "",
          validate: [VALIDATIONS.REQUIRED],
        },
      };
    },

    bulkActions () {
      return [
        {
          type: 'cancel',
          class: 'btn-default'
        },
        'edit'
      ]
    }
  },

  watch: {
    formErrors: {
      handler(value) {
        value &&
          import("vue").then((v) => {
            v.default.notify({
              group: "store_notifications",
              type: "error",
              text: value,
            });
          });
      },
      deep: true,
    },
  },

  mounted() {
    this.showLoading = true;

    this.callGetCountriesWithLoading();

    this.$nextTick(() => {
      this.scroll();
    });
    window.addEventListener("scroll", this.scroll);
  },

  beforeDestroy() {
    window.removeEventListener("scroll", this.scroll);
  },

  methods: {
    ...mapActions({
      getCountries: "admin/countries/getCountries",
      updateCountries: "admin/countries/updateCountries",
      updateCountryHouseholds: "admin/countries/updateCountryHouseholds",
      updateCategory: "admin/categories/updateCategory",
    }),
    onClearSearch() {
      this.name = "";
      this.callGetCountriesWithLoading();
    },
    onSearchChange(newValue) {
      this.pagination.page = 1;
      this.name = newValue;
      this.closeEditModal();
      this.callGetCountriesWithLoading();
    },
    callGetCountriesWithLoading() {
      this.showLoading = true;

      this.getCountries(this.dataGetCountries).finally(() => {
        this.showLoading = false;
      });
    },
    updateModel(model) {
      this.householdModel = model;
    },
    openEditForm() {
      this.householdModel.households = this.selectedRows[0].household;
      this.showEditPanel = true;
    },
    closeEditForm() {
      this.refreshTable();
      this.showEditPanel = false;
    },
    onSort({ option }) {
      this.pagination.page = 1;
      this.pagination = { ...this.pagination, ...option };
      this.callGetCountriesWithLoading(true);
    },
    onBulk() {
      this.callUpdateCountries({
        ids: this.selectedIds,
        households: this.householdModel.households,
      });

      this.refreshTable();
    },
    refreshTable() {
      this.keyTable++;
    },
    callUpdateCountries(data) {
      this.showLoading = true;
      data.actionTarget = this.actionTarget;

      if (this.actionTarget === BULK_UPDATE.ACTION_TARGET.FILTERED) {
        data.filters = { name: this.name };
        delete data.ids;
      }

      this.updateCountries({ projectId: this.projectId, data })
        .then(() => {
          this.closeEditModal();
        })
        .finally(() => {
          this.showLoading = false;
        });
    },
    resetPagination() {
      const { pagination } = this;
      this.pagination = {
        ...pagination,
        order: "created_at",
        sort: "desc",
        page: 1,
      };
    },
    onSortTable: async function ({ order, sort }) {
      const pagination = {
        ...this.pagination,
        order,
        sort,
        page: 1,
      };
      this.pagination = pagination;
    },
    buildRenderOptions() {
      return {};
    },
    updateRow(value, key, row) {
      if (row[key] == value) return;

      this.formErrors = null;

      if (key === "name") {
        this.formErrors = this.inputErrors(value, "not_null", "Name");
      }

      if (this.formErrors) return;

      const data = {
        ids: [row.id],
        households: value,
      };

      this.callUpdateCountries(data);
    },
    closeEditModal() {
      this.selectedRows = [];
      this.refreshTable();
      this.closeEditForm();
    },
    scroll() {
      const { projectId, categories, isBusy } = this;

      if (!this.countries || !this.countries.data.length) return;

      if (
        this.countries.pagination.currentPage <
        this.countries.pagination.totalPages
      ) {
        const wHeight = window.innerHeight;
        const {
          top,
          bottom,
          height,
        } = this.$refs.table.$el.getBoundingClientRect();
        const margin = 100;

        if (top * -1 + wHeight >= height - margin && isBusy == false) {
          this.pagination.page++;
          this.getCountries(this.dataGetCountries);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
label {
  color: $editor-gray;
}

.params-wrapper {
  min-height: 35px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid $editor-light-gray;
}

.loading-next-page {
  position: relative;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>